import {
    UserSession,
    UserAction,
    Source,
    ActionTypes,
    UserActionClick,
    UserActionConversion,
    UserActionPing,
    UserActionView,
    UserActionFilter,
    UserActionLoad,
    UserActionPageView,
    WidgetType,
} from 'rwl-shared/dist/models/analytics';

export type AnalyticsClientOptions = {
    flushQueueIntervalMs: number | 'never';
    maxQueueSize: number;
    queueStorageKey: string;
    onSendError: (error: Error) => void;
};

const DEFAULT_OPTIONS: AnalyticsClientOptions = {
    flushQueueIntervalMs: 10_000,
    maxQueueSize: 100,
    queueStorageKey: 'RWL:BI:QUEUE',
    onSendError: (error: Error) => {
        console.error(error);
    },
};

export class AnalyticsClient {
    private baseUrl: string;
    private options: AnalyticsClientOptions;
    private flushInProgress = false;
    private scheduleId: ReturnType<typeof setInterval> | null = null;

    constructor(
        baseUrl: string,
        options: Partial<AnalyticsClientOptions> = {}
    ) {
        this.baseUrl = baseUrl;
        this.options = { ...DEFAULT_OPTIONS, ...options };
        this.scheduleFlush();
    }

    /**
     * If you're done with the analytics client or need to initialize a new one, call this method first
     */
    destroy() {
        this.haltSchedule();
    }

    /**
     * Send all queued events immediately without waiting for the next scheduled interval
     *
     * Generally you want to use the create* methods and let the scheduler handle sending
     */
    async sendNow() {
        await this.flushQueue();
    }

    /**
     * Creates a new session, queues it to send to the server, and returns the session object
     *
     * NOTE: You probably only want one session at a time, we don't handle that here, it's up
     * to the caller to manage the session lifecycle
     */
    createSession(
        account: string,
        source: Source,
        integration: string | undefined,
        ipAddress: string | undefined,
        widgetType: WidgetType
    ) {
        const session = this.generateSession(
            account,
            source,
            integration,
            ipAddress,
            widgetType
        );
        this.pushToQueue(session);
        return session;
    }

    /**
     * Creates a new action, queues it to send to the server, and returns the action object
     *
     * This is the verbose but flexible version of the method, below are shorthands for specific action types
     */
    createAction<T extends UserAction>(
        type: T['segment']['type'],
        session: UserSession,
        property: string | undefined,
        data: T['data']
    ) {
        const action = this.generateAction(type, session, property, data);
        this.pushToQueue(action);
        return action;
    }

    createViewAction(
        session: UserSession,
        property: string | undefined,
        data: UserActionView['data']
    ) {
        return this.createAction(ActionTypes.VIEW, session, property, data);
    }

    createClickAction(
        session: UserSession,
        property: string | undefined,
        data: UserActionClick['data']
    ) {
        return this.createAction(ActionTypes.CLICK, session, property, data);
    }

    // A Load action is triggered every time a widget is loaded on the site
    createLoadAction(
        session: UserSession,
        property: string | undefined,
        data: UserActionLoad['data']
    ) {
        return this.createAction(ActionTypes.LOAD, session, property, data);
    }

    createFilterAction(
        session: UserSession,
        property: string | undefined,
        data: UserActionFilter['data']
    ) {
        return this.createAction(ActionTypes.FILTER, session, property, data);
    }

    // A Pageview action is triggered every time the plugin initialization code runs regardless
    // of whether the plugin is loaded on that page or not.
    // For example, if the plugin snippet is installed in the HEAD of every page on the site, this
    // would track visits for every page of the site even if the plugin is only loaded on a few pages.
    createPageViewAction(
        session: UserSession,
        property: string | undefined,
        data: UserActionPageView['data']
    ) {
        return this.createAction(ActionTypes.PAGEVIEW, session, property, data);
    }

    createConversionAction(
        session: UserSession,
        property: string | undefined,
        data: UserActionConversion['data']
    ) {
        return this.createAction(
            ActionTypes.CONVERSION,
            session,
            property,
            data
        );
    }

    createPingAction(
        session: UserSession,
        property: string | undefined,
        data: UserActionPing['data']
    ) {
        return this.createAction(ActionTypes.PING, session, property, data);
    }

    // Private methods

    private scheduleFlush() {
        if (this.options.flushQueueIntervalMs === 'never') {
            return;
        }
        this.scheduleId = setInterval(() => {
            this.flushQueue();
        }, this.options.flushQueueIntervalMs);
    }

    private haltSchedule() {
        if (this.scheduleId) {
            clearInterval(this.scheduleId);
        }
    }

    private pushToQueue(event: UserSession | UserAction) {
        const queueStorage = localStorage.getItem(this.options.queueStorageKey);
        const queueData = JSON.parse(queueStorage || '[]') as (
            | UserSession
            | UserAction
        )[];
        const updatedQueue = [...queueData, event];
        localStorage.setItem(
            this.options.queueStorageKey,
            JSON.stringify(updatedQueue)
        );
        if (updatedQueue.length > this.options.maxQueueSize) {
            this.flushQueue(); // Don't await, let it run in the background
        }
    }

    private async flushQueue() {
        // Use a lock to prevent multiple flushes from happening at the same time
        if (this.flushInProgress) {
            return;
        }
        this.flushInProgress = true;

        // Get the queue data from local storage
        const queueStorage = localStorage.getItem(this.options.queueStorageKey);
        const queueData = JSON.parse(queueStorage || '[]') as (
            | UserSession
            | UserAction
        )[];

        // Clear the queue before sending to the server so new events can start coming in
        localStorage.setItem(this.options.queueStorageKey, '[]');

        // Send the data to the server
        await this.sendEventData(queueData);

        // Remove the lock
        this.flushInProgress = false;
    }

    private async sendEventData(data: (UserSession | UserAction)[]) {
        if (data.length === 0) {
            return;
        }

        const url = `${this.baseUrl}/bi`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            this.options.onSendError(
                new Error(
                    `Failed to send analytics data: ${response.status} ${response.statusText}`
                )
            );
        }
    }

    private generateSession(
        account: string,
        source: Source,
        integration: string | undefined,
        ipAddress: string | undefined,
        widgetType: WidgetType
    ): UserSession {
        return {
            _id: this.getRandomId(),
            version: 2,
            collection: 'UserSession',
            capturedAt: new Date(),
            segment: {
                account,
                integration,
                source,
                widgetType,
            },
            data: {
                userAgent: navigator.userAgent,
                referrer: document.referrer,
                ipAddress,
            },
        };
    }

    private generateAction<T extends UserAction>(
        type: T['segment']['type'],
        session: UserSession,
        property: string | undefined,
        data: T['data']
    ): T {
        return {
            _id: this.getRandomId(),
            version: 2,
            collection: 'UserAction',
            capturedAt: new Date(),
            segment: {
                account: session.segment.account,
                session: session._id,
                property,
                type,
                source: session.segment.source,
                widgetType: session.segment.widgetType,
            },
            data,
        } as T;
    }

    private getRandomId() {
        if ('randomUUID' in self.crypto) {
            return self.crypto.randomUUID();
        } else {
            const date = Date.now().toString(16);
            // @ts-ignore
            const randomNumber: Uint8Array = self.crypto.getRandomValues(
                new Uint8Array(16)
            );
            const randomHex = Array.from(randomNumber)
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
            return `rid-${date}-${randomHex}`; // rid = random ID - hex encoded timestamp - hex encoded random number
        }
    }
}
